// Dependencies
import $axios, { setAuthToken } from '@martyrs/src/modules/core/views/utils/axios-instance.js';
import Cookies from 'js-cookie';
// Capacitor
import { Preferences } from '@capacitor/preferences';
// Vue modules
import { reactive, watch } from 'vue';
// Globals
import { useSession, setError } from '@martyrs/src/modules/core/views/store/core.store.js';
import wsManager from '@martyrs/src/modules/core/views/classes/ws.manager.js';

// State
const state = reactive({
  user: {
    _id: undefined,
    avatar: null,
    username: '',
    email: '',
    phone: '',
    password: '',
    passwordRepeat: '',
  },
  access: {
    roles: null,
    token: null,
    status: false,
  },
  accesses: [],
});

// Session manager
const session = useSession();

// Accesses-провайдер регистрирует внешний модуль (organizations) при своей
// инициализации: auth отвечает за личность и не знает, кто выдаёт полномочия.
// Нет провайдера — accesses остаются [] и никаких запросов не делается.
let accessesLoader = null;

function registerAccessesLoader(loader) {
  accessesLoader = loader;

  // Модули грузятся лениво по маршруту: организация может зарегистрироваться
  // ПОСЛЕ auth.initialize(). Тогда подтягиваем accesses сразу при регистрации.
  if (typeof window !== 'undefined' && state.access.status && !state.accesses.length) {
    (async () => {
      try {
        const accesses = await loader();
        state.accesses = accesses;
        const userCookie = await getCookie('user');
        if (userCookie) session.set({ ...userCookie, accesses });
      } catch (e) {
        // Недоступность accesses не критична — останутся []
      }
    })();
  }
}

const actions = {
  async initialize(cookie) {
    const userCookie = cookie ? cookie : await getCookie('user');

    if (!userCookie) {
      setAuthToken(null);
      session.clear();
      this.resetState();
      return;
    }

    // Optimistic-гидрация из куки: сервер и так валидирует токен на каждом
    // API-запросе (authJwt), поэтому доверяем куке сразу. SSR при этом не делает
    // HTTP-запрос сам к себе (check-token) — валидация только на клиенте, фоном.
    const { _id, email, phone, avatar, roles, accessToken } = userCookie;

    setAuthToken(accessToken);
    Object.assign(state.user, { _id, email, phone, avatar });
    Object.assign(state.access, { token: accessToken, roles, status: !!accessToken });
    session.set(userCookie);

    if (typeof window === 'undefined') return;

    try {
      // Валидация токена и сессии
      await $axios.get('/api/auth/check-token');

      // Accesses опциональны — их недоступность не повод разлогинивать.
      // Загрузчик регистрирует модуль полномочий (organizations); без него запроса нет.
      const accesses = accessesLoader ? await Promise.resolve(accessesLoader()).catch(() => []) : [];

      state.accesses = accesses;
      session.set({ ...userCookie, accesses });

      // Переподключаем WebSocket если пользователь аутентифицирован
      if (_id && accessToken) {
        await wsManager.reconnectWithAuth(_id);
      }
    } catch (error) {
      // Кука стирается ТОЛЬКО когда токен/сессия реально невалидны (401/403).
      // Сетевые ошибки и 5xx (деплой, рестарт, поднимающаяся база) куку не трогают:
      // состояние остаётся из куки, следующая загрузка ревалидирует.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        await removeCookie('user');
        setAuthToken(null);
        this.resetState();
      } else {
        console.warn('Auth revalidation skipped (transient error):', error.message);
      }
    }
  },

  // Helper method to reset state
  resetState() {
    state.accesses = [];
    state.user = {
      _id: undefined,
      avatar: null,
      username: '',
      email: '',
      phone: '',
      password: '',
      passwordRepeat: '',
    };
    state.access = {
      roles: null,
      token: null,
      status: false,
    };
    session.clear();
  },

  async login(user, type) {
    try {
      const response = await $axios.post('/api/auth/signin', {
        ...user,
        type,
      });

      setCookie('user', response.data);

      Object.assign(state.user, {
        ...response.data,
      });

      Object.assign(state.access, {
        token: response.data.accessToken,
        status: true,
        roles: response.data.roles,
      });

      session.set(response.data);

      await this.initialize();

      return response.data;
    } catch (error) {
      // Make sure cookie is removed on login error
      await removeCookie('user');

      Object.assign(state.access, {
        token: null,
        status: false,
        roles: null,
      });

      session.clear();

      setError(error);
      return Promise.reject(error);
    }
  },

  // Lead-логин (web2app-воронки): логиним lead-юзера, созданного проектным
  // эндпоинтом (напр. POST /api/auth/lead — passwordless-юзер по email), как
  // обычного martyrs-юзера: cookie `user` + auth-store. После этого lead может
  // платить до «настоящего» входа — платёжный модуль увидит валидный JWT/_id.
  // Эндпоинт должен вернуть { martyrsToken, martyrsUser, ...extra }; extra
  // (напр. leadUserId для последующей линковки аккаунтов) возвращаем вызывающему.
  async leadLogin(payload, endpoint = '/api/auth/lead') {
    const response = await $axios.post(endpoint, payload);
    const { martyrsToken, martyrsUser } = response.data || {};
    if (!martyrsToken || !martyrsUser) throw new Error('lead_auth_failed');

    // Тот же формат cookie, что у login(): martyrsUser + accessToken —
    // единый источник правды для SSR-init и authJwt middleware.
    const fullUser = { ...martyrsUser, accessToken: martyrsToken };
    setCookie('user', fullUser);

    // Поднимаем стор вручную (SPA-переход не запускает SSR-init). Ошибку
    // check-token не роняем наружу: cookie уже стоит, воронка продолжается.
    try {
      await this.initialize(fullUser);
    } catch (error) {
      console.error('Lead login initialize failed:', error);
    }

    return response.data;
  },

  async signup(user, type, invite) {
    const { password, passwordRepeat } = state.user;


    if (password !== passwordRepeat) {
      const error = {
        response: {
          data: { errorCode: 'PASSWORDS_DO_NOT_MATCH' },
        },
      };
      setError(error);
      throw new Error(error.response.status);
    }

    try {
      const response = await $axios.post('/api/auth/signup', {
        ...user,
        type,
        inviteCode: invite,
      });


      if (response.data.accessToken) {
        setCookie('user', response.data);
      }

      Object.assign(state.user, {
        ...response.data,
      });

      Object.assign(state.access, {
        token: response.data.accessToken,
        status: true,
        roles: response.data.roles,
      });

      session.set(response.data);

      await this.initialize();

      return response.data;
    } catch (error) {
      // Make sure cookie is removed on signup error
      await removeCookie('user');
      setError(error);
      throw error;
    }
  },

  async logout() {
    await removeCookie('user');
    setAuthToken(null);
    session.clear();
    this.resetState();

    // Отключаем WebSocket при выходе
    wsManager.disconnect();
  },
};

// Cookies
const optionsDefault = {
  development: { path: '/', domain: process.env.DOMAIN_URL, secure: false, expires: 30, sameSite: 'Lax' },
  production: {
    expires: 30,
    path: '/',
    domain: process.env.DOMAIN_URL,
    sameSite: 'Lax',
    secure: true,
  },
};

async function getCookie(name) {
  if (process.env.MOBILE_APP) {
    const { value } = await Preferences.get({ key: name });
    return value ? JSON.parse(value) : null;
  } else {
    const cookie = Cookies.get(name);
    return cookie ? JSON.parse(cookie) : null;
  }
}

async function setCookie(name, data, mode = process.env.NODE_ENV) {
  if (process.env.MOBILE_APP) {
    // Если приложение запущено в Capacitor
    await Preferences.set({
      key: name,
      value: JSON.stringify(data),
    });
  } else {
    // Для веб-версии
    const options = optionsDefault[mode];
    Cookies.set(name, JSON.stringify(data), options);
  }
}

async function removeCookie(name, mode = process.env.NODE_ENV) {
  if (process.env.MOBILE_APP) {
    // Если приложение запущено в Capacitor
    await Preferences.remove({ key: name });
  } else {
    // Для веб-версии
    const options = optionsDefault[mode];
    Cookies.remove(name, options);
  }
}

// History
const history = [];
history.push(state);

// Watch
watch(state, (newState, oldState) => {
  history.push(newState);
});

// Module Export
export { actions, removeCookie, state, registerAccessesLoader };
